import { useEffect, useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, PageTitle, btnGhost } from "@/admin/ui";

function QtyInput({ product, color, size, initial, onSaved }) {
  const [val, setVal] = useState(initial === undefined || initial === null ? "" : String(initial));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = val.trim();
    const qty = trimmed === "" ? null : parseInt(trimmed, 10);
    const orig = initial === undefined || initial === null ? null : initial;
    if (qty === orig) return;
    if (qty !== null && (Number.isNaN(qty) || qty < 0)) {
      toast.error("Quantity must be 0 or more (leave empty for untracked).");
      setVal(orig === null ? "" : String(orig));
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/stock/${product}`, { color, size, qty });
      toast.success(qty === null ? `${color} / ${size} set to untracked (always available).` : `${color} / ${size} → ${qty} in stock.`);
      onSaved(qty);
    } catch {
      toast.error("Could not update stock.");
      setVal(orig === null ? "" : String(orig));
    }
    setSaving(false);
  };

  const isZero = val.trim() === "0";
  const num = parseInt(val.trim(), 10);
  const isLow = !isZero && !Number.isNaN(num) && num > 0 && num <= 5;
  return (
    <input value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      placeholder="∞" inputMode="numeric" disabled={saving}
      data-testid={`stock-input-${product}-${color}-${size}`}
      className={`w-16 bg-transparent border px-2 py-1.5 font-mono text-xs text-center outline-none transition-colors disabled:opacity-50 ${
        isZero ? "border-destructive/70 text-destructive"
          : isLow ? "border-amber-500/70 text-amber-400"
          : "border-border text-chalk focus:border-signal"}`} />
  );
}

export default function Stock() {
  const [products, setProducts] = useState(null);
  const [filter, setFilter] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/stock").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/stock/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "sync-stock.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export stock."); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/stock/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [`${data.updated} quantities set`, `${data.untracked} untracked`];
      if (data.error_count) {
        toast.warning(`Imported with ${data.error_count} error(s): ${parts.join(", ")}. First: ${data.errors[0]}`);
      } else {
        toast.success(`Import complete — ${parts.join(", ")}.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed — check the CSV format.");
    }
    setImporting(false);
  };

  const setQty = (pid, color, size, qty) => {
    setProducts((ps) => ps.map((p) => {
      if (p.id !== pid) return p;
      const stock = { ...(p.stock || {}) };
      const colorMap = { ...(stock[color] || {}) };
      if (qty === null) delete colorMap[size]; else colorMap[size] = qty;
      stock[color] = colorMap;
      return { ...p, stock };
    }));
  };

  const shown = (products || []).filter((p) => p.name.toLowerCase().includes(filter.toLowerCase()));

  let lowCount = 0, outCount = 0;
  for (const p of products || []) {
    for (const colorMap of Object.values(p.stock || {})) {
      for (const q of Object.values(colorMap || {})) {
        if (q === 0) outCount += 1;
        else if (q > 0 && q <= 5) lowCount += 1;
      }
    }
  }

  return (
    <div data-testid="admin-stock-page">
      <PageTitle title="Stock" sub="Available quantity per product, color and size. Empty = untracked (always available). 0 = out of stock — customers cannot buy it. Quantities decrease automatically when orders are paid." />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter products…"
          data-testid="stock-filter-input"
          className="w-full sm:w-72 bg-transparent border border-border px-3 py-2.5 font-mono text-xs outline-none focus:border-signal" />
        <button onClick={exportCsv} className={btnGhost} data-testid="stock-export-btn">
          <Download size={13} /> Export CSV
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={importing} className={btnGhost} data-testid="stock-import-btn">
          <Upload size={13} /> {importing ? "Importing…" : "Import CSV"}
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" data-testid="stock-import-input" />
        <div className="flex items-center gap-4 sm:ml-auto font-mono text-[11px] uppercase tracking-wider">
          <span className={lowCount ? "text-amber-400" : "text-graphite"} data-testid="stock-summary-low">{lowCount} low stock</span>
          <span className={outCount ? "text-destructive" : "text-graphite"} data-testid="stock-summary-out">{outCount} out of stock</span>
        </div>
      </div>
      <p className="font-mono text-[10px] text-graphite mb-5 -mt-2">
        Bulk edit: Export CSV → fill the qty column (leave empty for untracked) → Import CSV. Low stock (1–5) shows amber here and an optional "Only X left" note for shoppers (toggle in Settings).
      </p>
      {products === null && <p className="font-mono text-xs text-graphite">Loading…</p>}
      <div className="space-y-4">
        {shown.map((p) => (
          <Card key={p.id} className="p-5 overflow-x-auto" data-testid={`stock-product-${p.id}`}>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-chalk mb-4">{p.name}</p>
            {(p.sizes || []).length === 0 ? (
              <p className="font-mono text-xs text-graphite">No sizes on this product.</p>
            ) : (
              <table className="min-w-[420px]">
                <thead>
                  <tr>
                    <th className="text-left font-mono text-[10px] uppercase tracking-wider text-graphite pb-2 pr-6">Color</th>
                    {p.sizes.map((s) => (
                      <th key={s} className="font-mono text-[10px] uppercase tracking-wider text-graphite pb-2 px-2 text-center">{s}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(p.colors || []).map((c) => (
                    <tr key={c.name} className="border-t border-border/40">
                      <td className="py-2 pr-6">
                        <span className="inline-flex items-center gap-2 font-mono text-xs text-chalk/80">
                          <span className="w-3 h-3 rounded-full border border-chalk/25 shrink-0" style={{ backgroundColor: c.hex }} />
                          {c.name}
                        </span>
                      </td>
                      {p.sizes.map((s) => (
                        <td key={s} className="py-2 px-2 text-center">
                          <QtyInput product={p.id} color={c.name} size={s}
                            initial={p.stock?.[c.name]?.[s]}
                            onSaved={(qty) => setQty(p.id, c.name, s, qty)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
